/**
 * Which columns a person sees, and the filter sets they have named.
 *
 * Both are per-browser rather than per-account, and deliberately so: they are
 * not data, they are how one person likes to look at data. Syncing them would
 * mean a sync table, a conflict story and a server migration to hold a
 * preference that costs nothing to set again. The trade is that they do not
 * follow someone to a second machine, which is the right way round.
 *
 * Replaced the earlier fixed "Key columns / All columns" switch, which was two
 * opinions about what matters imposed on everyone.
 */

const COLUMNS_KEY = 'mt_catalog_columns';
const VIEWS_KEY = 'mt_catalog_views';

/** Columns shown when nobody has chosen - the ones that identify and locate a tool. */
export const DEFAULT_COLUMNS = [
  'catalogNo',
  'photo',
  'project',
  'assetCode',
  'mouldName',
  'toolType',
  'cavities',
  'owner',
  'manufactureYear',
  'location',
  'qr',
  'engraved',
];

/**
 * Columns added after people may already have saved their own set. Each is
 * switched on once in a saved set, so nobody misses it; after that the saved
 * choice rules, and removing it again sticks.
 *
 * `mouldName` (24 Oct 2026): MOLD NAME became its own column, as on the client's
 * sheet; until then the name sat in the code column, so a saved set without it
 * would lose the names.
 */
const ADDED_LATER = ['engraved', 'mouldName'];
const ADDED_KEY = 'mt_catalog_columns_added';

/** A named filter set, saved by one person on one browser. */
export interface SavedView {
  id: string;
  name: string;
  search: string;
  projectId: string;
  ownerCompanyId: string;
  toolTypeId: string;
  /** Columns as they were when the view was saved. Omitted means "leave as-is". */
  columns?: string[];
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback; // a corrupt preference is a missing preference, never a crash
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked. The in-memory state still works for this session.
  }
}

export function readColumns(): string[] | null {
  const stored = read<string[] | null>(COLUMNS_KEY, null);
  if (!(Array.isArray(stored) && stored.length > 0)) return null;
  const added = read<string[]>(ADDED_KEY, []);
  const fresh = ADDED_LATER.filter((k) => !added.includes(k));
  if (fresh.length === 0) return stored;
  const next = [...stored, ...fresh.filter((k) => !stored.includes(k))];
  write(COLUMNS_KEY, next);
  write(ADDED_KEY, [...added, ...fresh]);
  return next;
}

export function writeColumns(keys: string[]): void {
  write(COLUMNS_KEY, keys);
}

export function readViews(): SavedView[] {
  const stored = read<SavedView[]>(VIEWS_KEY, []);
  return Array.isArray(stored) ? stored.filter((v) => v && typeof v.name === 'string') : [];
}

export function writeViews(views: SavedView[]): void {
  write(VIEWS_KEY, views);
}
